import { FORMAT_HTTP_HEADERS, Tags } from 'opentracing';
import { HttpMethod } from './types/http-util';
import { withDedupe } from './utils/dedupe';
import { fetch } from './utils/fetch';
import { getQueryString } from './utils/query';
import { withTimeout, TimeoutError } from './utils/timeout';
import { CONTENT_TYPE_DEFAULT, CONTENT_TYPE_TEXT, HEADER_ACCEPT_LANGUAGE, HEADER_APPDOMAIN, HEADER_AUTHORIZATION, HEADER_CLIENT_IP, HEADER_CLIENT_TYPE, HEADER_CONTENT_TYPE, HEADER_CSRF, HEADER_FLOW_ID, HEADER_SALES_CHANNEL, HEADER_ZALANDO_CLIENT_ID, } from './constants';
import { noopLogger } from './noop-logger';
import { assertStatusCodes } from './validators';
const DEFAULT_TIMEOUT_MS = 5000;
export class Request {
    constructor({ path, method }) {
        this.logger = noopLogger;
        this.baseUrl = '';
        this.validators = [];
        this.dedupeRequests = false;
        this.options = {
            path,
            method,
            timeoutMS: DEFAULT_TIMEOUT_MS,
            queryParameters: {},
            headers: {},
        };
    }
    withHeader(name, value) {
        this.options.headers[name] = value.toString();
        return this;
    }
    withHeaders(requestHeaders) {
        Object.keys(requestHeaders).forEach(key => {
            this.withHeader(key, requestHeaders[key]);
        });
        return this;
    }
    acceptStatusCodes(acceptedStatusCodes) {
        this.validators.push(assertStatusCodes(acceptedStatusCodes));
        return this;
    }
    withValidator(validator) {
        this.validators.push(validator);
        return this;
    }
    withValidators(validators) {
        this.validators = [...this.validators, ...validators];
    }
    withAbortController(controller) {
        this.abortController = controller;
        return this;
    }
    withAppDomainId(appDomainId) {
        this.withHeader(HEADER_APPDOMAIN, appDomainId.toString());
        return this;
    }
    withAuthentication(accessToken, type = 'Bearer') {
        this.withHeader(HEADER_AUTHORIZATION, `${type} ${accessToken}`);
        return this;
    }
    withCredentials(mode) {
        this.credentials = mode;
        return this;
    }
    withClientId(clientId) {
        this.withHeader(HEADER_ZALANDO_CLIENT_ID, clientId);
        return this;
    }
    withClientIP(clientIP) {
        this.withHeader(HEADER_CLIENT_IP, clientIP);
        return this;
    }
    withClientType(clientType) {
        this.withHeader(HEADER_CLIENT_TYPE, clientType);
        return this;
    }
    withFlowID(flowId) {
        this.withHeader(HEADER_FLOW_ID, flowId);
        return this;
    }
    withSalesChannel(salesChannel) {
        this.withHeader(HEADER_SALES_CHANNEL, salesChannel);
        return this;
    }
    withLocale(locale) {
        this.withHeader(HEADER_ACCEPT_LANGUAGE, locale);
        return this;
    }
    withCsrf(token) {
        this.withHeader(HEADER_CSRF, token);
        return this;
    }
    withBaseUrl(baseUrl) {
        this.baseUrl = baseUrl;
        return this;
    }
    withLogger(logger) {
        this.logger = logger;
        return this;
    }
    withQueryParameters(queryParameters) {
        Object.entries(queryParameters)
            .filter(([, value]) => typeof value !== 'undefined')
            .forEach(([key, value]) => {
            this.options.queryParameters[key] = value;
        });
        return this;
    }
    withTimeout(timeoutMS) {
        this.options.timeoutMS = timeoutMS;
        return this;
    }
    /**
     * Do not use requests deduplication on the server side requests for requests that are customer specific.
     *
     * If you really want to do that, use the `customKey` that will contain some unique user identifier.
     *
     * @param {string|function} [customKeyOrCallback] Unique key used to resolve multiple execution to a single request. By default, path and query parameters are used.
     */
    withRequestDeduplication(customKeyOrCallback) {
        if (typeof customKeyOrCallback === 'function') {
            const { body, headers, method, path, queryParameters } = this.options;
            const options = {
                baseUrl: this.baseUrl,
                body,
                headers,
                method,
                path,
                queryParameters,
            };
            if (this.credentials) {
                options.credentials = this.credentials;
            }
            this.dedupeRequestsKey = customKeyOrCallback(options);
        }
        else {
            this.dedupeRequestsKey = customKeyOrCallback;
        }
        this.dedupeRequests = true;
        return this;
    }
    withTracer(tracer, { serviceId, parentSpan, spanName }) {
        this.options.tracer = tracer;
        this.options.serviceId = serviceId;
        this.options.parentSpan = parentSpan;
        this.options.spanName = spanName;
        return this;
    }
    getDeduplicationKey(url) {
        var _a;
        return (_a = this.dedupeRequestsKey) !== null && _a !== void 0 ? _a : url;
    }
    getQueryString() {
        return getQueryString(this.options.queryParameters);
    }
    getRequestBody() {
        const { body } = this.options;
        return typeof body !== 'string' ? JSON.stringify(body) : body;
    }
    getSpan() {
        if (!this.options.tracer) {
            return;
        }
        const { method } = this.options;
        const spanName = this.options.spanName || `API - ${method}`;
        const parentSpan = this.options.parentSpan ? { childOf: this.options.parentSpan } : {};
        const span = this.options.tracer.startSpan(spanName, parentSpan);
        const url = `${this.baseUrl}${this.options.path}`;
        span.setTag(Tags.HTTP_URL, url);
        span.setTag(Tags.HTTP_METHOD, method);
        span.setTag(Tags.SPAN_KIND, Tags.SPAN_KIND_RPC_CLIENT);
        span.setTag(Tags.PEER_SERVICE, this.options.serviceId);
        span.setTag(Tags.PEER_HOSTNAME, this.baseUrl);
        this.options.tracer.inject(span.context(), FORMAT_HTTP_HEADERS, this.options.headers);
        return span;
    }
    isDeduplicationEnabled(method) {
        return this.dedupeRequests && method === HttpMethod.GET;
    }
    async send() {
        const queryString = this.getQueryString();
        const url = `${this.baseUrl}${this.options.path}${queryString}`;
        const { body, method, headers, timeoutMS, queryParameters } = this.options;
        const span = this.getSpan();
        this.logger.info(`${method}: ${url}`);
        if (body) {
            this.logger.trace('with payload', body);
        }
        this.logger.trace('with params', {
            headers,
            queryParameters,
            timeoutMS,
        });
        const fetchWithTimeout = withTimeout(fetch);
        const fetchWithTimeoutAndDedupe = withDedupe(fetchWithTimeout);
        const response = await fetchWithTimeoutAndDedupe(url, {
            dedupe: this.isDeduplicationEnabled(method),
            dedupeKey: this.getDeduplicationKey(url),
            method,
            headers,
            ...(this.credentials && { credentials: this.credentials }),
            timeoutMS,
            body: this.getRequestBody(),
            ...(this.abortController && {
                controller: this.abortController,
                signal: this.abortController.signal,
            }),
        }).catch(e => {
            // fetch error handler to close a span before throwing
            if (span) {
                span.log({ event: 'error', e });
                span.setTag(Tags.ERROR, true);
                span.finish();
            }
            this.logger.error(`Error occurred for a ${method} on ${url} =>`, e);
            if (this.abortController &&
                // The `reason` property should be supported from Node v17.2.0 and v16.14.0.
                // Our types in this project does not reflect these versions.
                // @ts-ignore
                this.abortController.signal.reason instanceof TimeoutError) {
                // This will throw an error forwarded as a `reason` to `.abort()`.
                // @ts-ignore
                this.abortController.signal.throwIfAborted();
            }
            throw e;
        });
        if (span) {
            span.setTag(Tags.HTTP_STATUS_CODE, response.status);
        }
        // inject a clean context each time
        this.validators.forEach(validator => validator({ span, logger: this.logger, response, method }));
        if (span) {
            span.finish();
        }
        return response;
    }
    __getValuesForTesting__() {
        const { method, headers, path } = this.options;
        return {
            baseUrl: this.baseUrl,
            query: this.getQueryString(),
            body: this.getRequestBody(),
            headers,
            method,
            path,
        };
    }
}
export class RequestWithPayload extends Request {
    withPayload(body) {
        this.options.body = body;
        if (!this.options.headers[HEADER_CONTENT_TYPE]) {
            this.withHeaders({
                [HEADER_CONTENT_TYPE]: typeof body === 'string' ? CONTENT_TYPE_TEXT : CONTENT_TYPE_DEFAULT,
            });
        }
        return this;
    }
}
//# sourceMappingURL=request.js.map